import { Anchor, Blockquote, Box, Checkbox, Code, Divider, Image, List, Table, Text, Title } from "@mantine/core";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

export type MarkdownSize = "xs" | "sm" | "md" | "lg";

export interface MarkdownProps {
  /** The markdown source (GitHub-flavoured — tables, strikethrough, task
   *  lists). Raw HTML in the source is never rendered as elements — it comes
   *  out as plain, escaped text, the same as react-markdown's own default. */
  children: string;
  /**
   * Base size for paragraphs, list items and table cells, on Mantine's `fz`
   * scale. Headings use `Title`'s own `order`-based sizing regardless.
   * @default "sm"
   */
  size?: MarkdownSize;
  /**
   * Tightens paragraph/list/table spacing for dense, inline contexts — a
   * single changelog entry among many, rather than a standalone article.
   * @default false
   */
  compact?: boolean;
  className?: string;
}

const LINE_HEIGHT: Record<MarkdownSize, number> = { xs: 1.5, sm: 1.6, md: 1.6, lg: 1.5 };

// Anything that isn't a same-document link (relative, hash, mailto:, tel:)
// is treated as leaving the app and gets a safe new-tab target.
function isExternalHref(href: string | undefined): boolean {
  if (!href) return false;
  if (href.startsWith("#") || href.startsWith("/")) return false;
  if (/^(mailto|tel):/i.test(href)) return false;
  return /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//");
}

/**
 * Shared GitHub-flavoured Markdown renderer for Nodus article content — FAQ
 * articles (operator-written, portal and admin) and changelog entries. Maps
 * every block/inline element onto its Mantine equivalent (`frontend/CLAUDE.md`
 * and `tenant-frontend/CLAUDE.md` forbid raw `<p>`/`<h*>`/`<ul>`/`<a>`/`<hr>`),
 * so the output always matches the house typography rather than the
 * browser's own UA defaults.
 *
 * Links get `target="_blank" rel="noopener noreferrer"` when they leave the
 * app; images are responsive (`width: 100%`, Mantine's `Image`); raw HTML in
 * the source is never injected as elements.
 */
export function Markdown({ children, size = "sm", compact = false, className }: MarkdownProps) {
  const lh = LINE_HEIGHT[size];
  const blockMb = compact ? 4 : "sm";

  const components: Components = {
    p: ({ children }) => (
      <Text fz={size} lh={lh} m={0} mb={blockMb}>
        {children}
      </Text>
    ),
    h1: ({ children }) => (
      <Title order={1} mt="md" mb="sm">
        {children}
      </Title>
    ),
    h2: ({ children }) => (
      <Title order={2} mt="md" mb="sm">
        {children}
      </Title>
    ),
    h3: ({ children }) => (
      <Title order={3} mt="md" mb="xs">
        {children}
      </Title>
    ),
    h4: ({ children }) => (
      <Title order={4} mt="sm" mb="xs">
        {children}
      </Title>
    ),
    h5: ({ children }) => (
      <Title order={5} mt="sm" mb="xs">
        {children}
      </Title>
    ),
    h6: ({ children }) => (
      <Title order={6} mt="sm" mb="xs">
        {children}
      </Title>
    ),
    ul: ({ children }) => (
      <List fz={size} lh={lh} spacing={4} mb={blockMb}>
        {children}
      </List>
    ),
    ol: ({ children, start }) => (
      <List type="ordered" fz={size} lh={lh} spacing={4} mb={blockMb} start={start}>
        {children}
      </List>
    ),
    li: ({ children, className: liClassName }) => {
      // remark-gfm marks a task-list `<li>` with `className="task-list-item"`
      // and prepends a checkbox `<input>` (handled below) — suppress the
      // native bullet for just that item so the checkbox reads as the marker.
      const isTask = typeof liClassName === "string" && liClassName.includes("task-list-item");
      return <List.Item style={isTask ? { listStyleType: "none" } : undefined}>{children}</List.Item>;
    },
    // A GFM task-list checkbox — the only `<input>` markdown ever produces.
    // Rendered disabled (ticking it isn't wired to anything yet; the FAQ/
    // changelog source is the only place state can change) and styled like
    // every other Nodus checkbox rather than a bare native control.
    input: ({ type, checked }) =>
      type === "checkbox" ? <Checkbox checked={!!checked} disabled readOnly size="xs" mr={6} /> : null,
    a: ({ href, children }) => {
      const external = isExternalHref(href);
      return (
        <Anchor href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>
          {children}
        </Anchor>
      );
    },
    img: ({ src, alt }) =>
      typeof src === "string" ? <Image src={src} alt={alt ?? ""} radius="sm" my="xs" /> : null,
    hr: () => <Divider my="md" />,
    blockquote: ({ children }) => <Blockquote mb={blockMb}>{children}</Blockquote>,
    table: ({ children }) => (
      <Table fz={size} withTableBorder withColumnBorders mb={blockMb}>
        {children}
      </Table>
    ),
    thead: ({ children }) => <Table.Thead>{children}</Table.Thead>,
    tbody: ({ children }) => <Table.Tbody>{children}</Table.Tbody>,
    tr: ({ children }) => <Table.Tr>{children}</Table.Tr>,
    // GFM column alignment (`:--`, `:-:`, `--:`) comes through as an inline
    // `style` on `th`/`td` — forward it or alignment silently breaks.
    th: ({ children, style }) => <Table.Th style={style}>{children}</Table.Th>,
    td: ({ children, style }) => <Table.Td style={style}>{children}</Table.Td>,
    // No syntax highlighter here, and plain react-markdown gives no reliable
    // signal for "this `code` is a fenced block" (no `inline` prop since v8,
    // no `className` without a language tag) — a multi-line body is the
    // practical tell. `pre` is left to the `Code` it wraps: with `block`,
    // `Code` renders its own single `<pre>`, so the outer `pre` is a no-op.
    code: ({ children, className: codeClassName }) => {
      const text = String(children);
      const block = text.includes("\n");
      return (
        <Code block={block} className={codeClassName}>
          {block ? text.replace(/\n$/, "") : text}
        </Code>
      );
    },
    pre: ({ children }) => <>{children}</>,
  };

  return (
    <Box className={className}>
      {/* `urlTransform` is react-markdown's own default — set explicitly so the
          guarantee it provides (javascript:/data:/vbscript: hrefs and srcs are
          neutered to "", only https?/ircs?/mailto/xmpp pass through) is visible
          here rather than relying silently on a default that could change. */}
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={defaultUrlTransform}>
        {children}
      </ReactMarkdown>
    </Box>
  );
}
